import React, { useState, useEffect, useMemo } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Select, 
  MenuItem, 
  IconButton,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { billingService } from '../../Services/BillingService';
import easypaisaLogo from '../../assets/Easypaisa-logo.png';
import './BillingManager.css';
import '../../UI/Common.css';

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export default function BillingManager() {
  const [history, setHistory] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    billingMonth: new Date().getMonth() + 1,
    billingYear: new Date().getFullYear(),
    amount: '',
    transactionReference: '',
    transactionMessage: '',
    notes: ''
  });

  const laboratoryId = sessionStorage.getItem('LabID');

  const fetchBillingData = async () => {
    if (!laboratoryId) return;

    try {
      setLoading(true);
      const [historyData, subData] = await Promise.all([
        billingService.getBillingHistory(laboratoryId),
        billingService.getSubscriptionDetails(laboratoryId).catch((err) => {
          console.warn("No subscription found:", err);
          return null;
        })
      ]);

      setHistory(historyData);
      setSubscription(subData);
    } catch (error) {
      console.error("Failed to fetch billing data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingData();
  }, [laboratoryId]);

  // Generate all expected billing months from subscription StartDate up to Current Month
  const billingSchedule = useMemo(() => {
    if (!subscription || !subscription.startDate) return [];

    const schedule = [];
    const startDate = new Date(subscription.startDate);
    const currentDate = new Date();

    let iterDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

    while (iterDate <= endDate) {
      const monthVal = iterDate.getMonth() + 1;
      const yearVal = iterDate.getFullYear();

      const paymentRecord = history.find(
        (h) => h.billingMonth === monthVal && h.billingYear === yearVal
      );

      schedule.push({
        month: monthVal,
        year: yearVal,
        monthName: months[monthVal - 1],
        payment: paymentRecord || null,
        isPaid: paymentRecord && (paymentRecord.status === 'Verified' || paymentRecord.status === 'Pending')
      });

      iterDate.setMonth(iterDate.getMonth() + 1);
    }

    return schedule.reverse();
  }, [subscription, history]);

  const currentMonthInfo = useMemo(() => {
    const now = new Date();
    const currM = now.getMonth() + 1;
    const currY = now.getFullYear();

    return billingSchedule.find(s => s.month === currM && s.year === currY);
  }, [billingSchedule]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpenModalForMonth = (month, year) => {
    setFormData((prev) => ({
      ...prev,
      billingMonth: month,
      billingYear: year,
      amount: subscription ? subscription.amount : prev.amount
    }));
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.transactionMessage) {
      alert("Please fill in the amount and transaction message.");
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        laboratoryId: parseInt(laboratoryId, 10),
        billingMonth: parseInt(formData.billingMonth, 10),
        billingYear: parseInt(formData.billingYear, 10),
        amount: parseFloat(formData.amount),
        transactionReference: formData.transactionReference,
        transactionMessage: formData.transactionMessage,
        notes: formData.notes
      };

      await billingService.submitBilling(payload);
      setShowModal(false);
      
      setFormData({
        billingMonth: new Date().getMonth() + 1,
        billingYear: new Date().getFullYear(),
        amount: '',
        transactionReference: '',
        transactionMessage: '',
        notes: ''
      });

      fetchBillingData();
    } catch (error) {
      console.error("Failed to submit billing:", error);
      alert("Submission failed. Check console for details.");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Verified':
        return 'status-badge verified';
      case 'Rejected':
        return 'status-badge rejected';
      case 'Pending':
        return 'status-badge pending';
      default:
        return 'status-badge rejected';
    }
  };

  return (
    <div className="container-fluid Container2 billing-container common-FontFamily">

      {/* Subscription Card Section */}
      {subscription && (
        <div className="subscription-card common-box-shadow common-box-border common-bg-white mb-4 p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="common-FontWeight m-0 text-primary">Active Subscription Details</h5>
            <span className={`status-badge ${currentMonthInfo?.isPaid ? 'verified' : 'rejected'}`}>
              {currentMonthInfo?.isPaid 
                ? `Current Month: ${currentMonthInfo.payment.status}` 
                : 'Current Month: Unpaid'}
            </span>
          </div>
          <div className="row mt-3">
            <div className="col-md-3">
              <span className="common-Labels d-block">Billing Cycle:</span>
              <strong className="common-FontSize-small">{subscription.billingCycle}</strong>
            </div>
            <div className="col-md-3">
              <span className="common-Labels d-block">Monthly Amount:</span>
              <strong className="common-FontSize-small">{subscription.amount.toFixed(2)}</strong> <span className="common-FontSize-small">PKR</span>
            </div>
            <div className="col-md-3">
              <span className="common-Labels d-block">Subscription Start:</span>
              <strong className="common-FontSize-small">
                {new Date(subscription.startDate).toLocaleDateString()}
              </strong>
            </div>
            <div className="col-md-3">
              <span className="common-Labels d-block">Next Billing Due:</span>
              <strong className="common-FontSize-small text-danger">
                {new Date(subscription.nextDueDate).toLocaleDateString()}
              </strong>
            </div>
          </div>

          {/* Payment Instructions Banner (English & Urdu) */}
          <div className="p-3 border rounded bg-light border-info mt-4">
            <div className="d-flex align-items-start">
              <InfoOutlinedIcon className="text-info me-2 mt-1" fontSize="small" />
              <div>
                <strong className="common-FontSize-small text-dark d-block mb-1">
                  Payment Guide / ادائیگی کی آسان ہدایات:
                </strong>
                <p className="common-FontSize-small text-secondary mb-1">
                  <strong>English:</strong> Please transfer the payment to one of the provided accounts below. Once transferred, click the <strong>"Pay Now"</strong> button in the schedule table below, paste the transaction SMS/receipt text you received, and submit the form.
                </p>
                <p className="common-FontSize-small text-secondary mb-0" style={{ direction: 'rtl', textAlign: 'right' }}>
                  <strong>اردو:</strong> براہ کرم نیچے دیے گئے کسی بھی ایک اکاؤنٹ میں رقم منتقل کریں۔ ادائیگی کے بعد، نیچے دی گئی لسٹ میں <strong>"Pay Now"</strong> کے بٹن پر کلک کریں، موصول شدہ ٹرانزیکشن SMS یہاں پیسٹ کریں اور فارم جمع کرائیں۔
                </p>
              </div>
            </div>
          </div>

          {/* Payment Deposit Accounts Section */}
          <div className="mt-4 pt-3 border-top">
            <h6 className="common-FontWeight text-secondary mb-3 d-flex align-items-center">
              <AccountBalanceIcon className="me-2 text-primary" fontSize="small" />
              Payment Deposit Account Details / اکاؤنٹ کی تفصیلات
            </h6>
            <div className="row g-3">
              
              {/* Easypaisa Account Box */}
              <div className="col-md-6">
                <div className="p-3 border rounded bg-light h-100">
                  <div className="d-flex align-items-center mb-2">
                    <img 
                      src={easypaisaLogo} 
                      alt="Easypaisa Logo" 
                      style={{ height: '32px', width: 'auto', marginRight: '10px', objectFit: 'contain' }} 
                    />
                    <strong className="common-FontSize-small text-success">Easypaisa Account</strong>
                  </div>
                  <div className="common-FontSize-small mt-2">
                    <div><span className="text-muted">Account Title:</span> <strong>WAQIF ALI</strong></div>
                    <div><span className="text-muted">Account / Mobile No:</span> <strong>03089394272</strong></div>
                  </div>
                </div>
              </div>

              {/* Meezan Bank Account Box */}
              <div className="col-md-6">
                <div className="p-3 border rounded bg-light h-100">
                  <div className="d-flex align-items-center mb-2">
                    <img 
                      src="https://www.meezanbank.com/wp-content/themes/mbl/images/logo.png" 
                      alt="Meezan Bank Logo" 
                      style={{ height: '32px', width: 'auto', marginRight: '10px', objectFit: 'contain' }} 
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <strong className="common-FontSize-small text-primary">Meezan Bank Limited</strong>
                  </div>
                  <div className="common-FontSize-small mt-2">
                    <div><span className="text-muted">Account Title:</span> <strong>WAQIF ALI</strong></div>
                    <div><span className="text-muted">Account Number:</span> <strong>03020110267610</strong></div>
                    <div><span className="text-muted">IBAN:</span> <strong>PK39MEZN0003020110267610</strong></div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Banner alert */}
          {(!currentMonthInfo || !currentMonthInfo.isPaid) && (
            <div className="alert alert-warning d-flex align-items-center mt-3 mb-0 py-2" role="alert">
              <WarningAmberIcon className="me-2" />
              <span className="common-FontSize-small">
                Payment for <strong>{months[new Date().getMonth()]} {new Date().getFullYear()}</strong> is pending. Please transfer the amount to one of the accounts above and submit details below.
              </span>
            </div>
          )}
          {currentMonthInfo?.isPaid && (
            <div className="alert alert-success d-flex align-items-center mt-3 mb-0 py-2" role="alert">
              <CheckCircleOutlineIcon className="me-2" />
              <span className="common-FontSize-small">
                Current month payment recorded ({currentMonthInfo.payment.status}).
              </span>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="billing-header">
        <h2 className="billing-title common-FontWeight">Subscription Billing Schedule & History</h2>
      </div>

      {/* Complete Monthly Breakdown Table */}
      {loading ? (
        <p className="common-FontSize-small">Loading billing schedule...</p>
      ) : (
        <div className="table-wrapper common-box-shadow common-box-border common-bg-white">
          <table className="billing-table">
            <thead>
              <tr className="common-background-color">
                <th className="common-Labels">Billing Period</th>
                <th className="common-Labels">Expected Amount</th>
                <th className="common-Labels">Txn Ref ID</th>
                <th className="common-Labels">Transaction Message</th>
                <th className="common-Labels">Status</th>
                <th className="common-Labels">Action / Submitted On</th>
              </tr>
            </thead>
            <tbody>
              {billingSchedule.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-row common-text-center common-FontSize-small">
                    No active subscription found to generate schedule.
                  </td>
                </tr>
              ) : (
                billingSchedule.map((item) => (
                  <tr key={`${item.year}-${item.month}`}>
                    <td className="common-FontSize-small">
                      <strong className="commont-Font-Bold">{item.monthName} {item.year}</strong>
                    </td>
                    <td className="common-FontSize-small">
                      {subscription ? subscription.amount.toFixed(2) : '0.00'}
                    </td>
                    <td className="common-FontSize-small">
                      {item.payment ? (item.payment.transactionReference || 'N/A') : '-'}
                    </td>
                    <td className="message-cell common-FontSize-small">
                      {item.payment ? item.payment.transactionMessage : '-'}
                    </td>
                    <td>
                      <span className={`${getStatusBadgeClass(item.payment ? item.payment.status : 'Unpaid')} common-Font-10`}>
                        {item.payment ? item.payment.status : 'Unpaid'}
                      </span>
                    </td>
                    <td className="date-cell common-FontSize-small">
                      {item.payment ? (
                        new Date(item.payment.createdAt).toLocaleDateString()
                      ) : (
                        <Button 
                          size="small" 
                          variant="outlined"
                          color="primary"
                          onClick={() => handleOpenModalForMonth(item.month, item.year)}
                          style={{ fontSize: '11px', padding: '2px 8px' }}
                        >
                          Pay Now
                        </Button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Material UI Dialog (Modal) */}
      <Dialog 
        open={showModal} 
        onClose={() => setShowModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: 'common-modal common-br-3px' }}
      >
        <DialogTitle className="common-FontFamily common-FontWeight common-FontSize-small" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Add Monthly Payment Record / ماہانہ ادائیگی کا ریکارڈ درج کریں */}
          Add Monthly Payment Record 
          <IconButton onClick={() => setShowModal(false)} size="small" className="common-cross-button">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            
            {/* Modal Instruction Header */}
            <div className="alert alert-info py-2 common-FontSize-small mb-3">
              <div><strong>Instructions:</strong> Paste the complete SMS message received from bank/Easypaisa to verify payment.</div>
              <div dir="rtl" style={{ textAlign: 'right' }}><strong>ہدایت:</strong> بینک یا ایزی پیسہ سے موصول شدہ پورا SMS پیغام یہاں درج کریں۔</div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="common-Labels">
                  Month / مہینہ <span className="common-Asterisk">*</span>
                </label>
                <Select
                  name="billingMonth"
                  value={formData.billingMonth}
                  onChange={handleChange}
                  size="small"
                  fullWidth
                  className="common-Input"
                >
                  {months.map((m, idx) => (
                    <MenuItem key={idx + 1} value={idx + 1} className="common-FontSize-small">
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </div>

              <div className="form-group">
                <label className="common-Labels">
                  Year / سال <span className="common-Asterisk">*</span>
                </label>
                <input
                  type="number"
                  name="billingYear"
                  value={formData.billingYear}
                  onChange={handleChange}
                  className="form-control common-Input"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="common-Labels">
                Amount Paid / بھیجی گئی رقم <span className="common-Asterisk">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                name="amount"
                placeholder="0.00"
                value={formData.amount}
                onChange={handleChange}
                className="form-control common-Input"
                required
              />
            </div>

            <div className="form-group">
              <label className="common-Labels">
                Transaction Reference / TRX ID (Optional) / ٹرانزیکشن ID
              </label>
              <input
                type="text"
                name="transactionReference"
                placeholder="e.g. TXN12345678"
                value={formData.transactionReference}
                onChange={handleChange}
                className="form-control common-Input"
              />
            </div>

            <div className="form-group">
              <label className="common-Labels">
                Transaction Message / SMS / موصول شدہ SMS پیغام <span className="common-Asterisk">*</span>
              </label>
              <textarea
                name="transactionMessage"
                rows={3}
                placeholder="Paste bank confirmation SMS or transaction receipt text here... / موصول شدہ SMS پیغام یہاں پیسٹ کریں"
                value={formData.transactionMessage}
                onChange={handleChange}
                className="form-control common-Textarea"
                required
              />
              <small className="text-muted d-block mt-1 common-FontSize-small">
                Please paste the exact SMS text received on your mobile upon transferring money.
              </small>
            </div>

            <div className="form-group">
              <label className="common-Labels">Notes (Optional) / اضافی معلومات</label>
              <input
                type="text"
                name="notes"
                placeholder="Additional details..."
                value={formData.notes}
                onChange={handleChange}
                className="form-control common-Input"
              />
            </div>
          </DialogContent>

          <DialogActions style={{ padding: '12px 24px' }}>
            <Button 
              type="button" 
              variant="outlined"
              className="common-setting-button common-cursor" 
              onClick={() => setShowModal(false)}
            >
              Cancel 
            </Button>
            <Button
              type="submit"
              variant="contained"
              className="common-button common-cursor"
              disabled={submitting}
              style={{ height: '30px', margin: 0 }}
            >
              {submitting ? 'Submitting...' : 'Submit Payment'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

    </div>
  );
}